import { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../api';
import { UserContext } from '../context/UserContext';

const PLACEMENTS = [
  // Note: keep eggs out of the left 240px sidebar area on desktop.
  { match: '/leaderboard', eggKey: 'egg-01', style: { top: 'calc(var(--header-h) + 260px)', left: 'calc(240px + 28px)' } },
  { match: '/downloads', eggKey: 'egg-02', style: { top: 'calc(var(--header-h) + 420px)', right: 22 } },
  { match: '/apn-clips/voting', eggKey: 'egg-03', style: { top: 'calc(var(--header-h) + 200px)', right: 34 } },
  { match: '/community-calendar', eggKey: 'egg-04', style: { bottom: 190, left: 'calc(240px + 88px)' } },
  { match: '/lurker', eggKey: 'egg-05', style: { top: 'calc(var(--header-h) + 340px)', right: 16 } },
  { match: '/kreativ-studio', eggKey: 'egg-06', style: { bottom: 260, right: 64 } },
  { match: '/datenschutz', eggKey: 'egg-07', style: { top: 'calc(var(--header-h) + 140px)', right: 92 } },
  { match: '/mitglieder', eggKey: 'egg-08', style: { bottom: 210, left: 'calc(240px + 18px)' } },
  { match: '/mentoren-programm', eggKey: 'egg-09', style: { top: 'calc(var(--header-h) + 420px)', right: 18 } },
  { match: '/profile', eggKey: 'egg-10', style: { bottom: 160, right: 18 } },
];

export default function EasterEggHuntOverlay() {
  const { user } = useContext(UserContext);
  const { pathname } = useLocation();

  const [loading, setLoading] = useState(true);
  const [live, setLive] = useState({ active: false, enabled: false, window: null });
  const [foundKeys, setFoundKeys] = useState([]);
  const [saving, setSaving] = useState(false);
  const [imgOk, setImgOk] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!user) return;
      setLoading(true);
      try {
        const res = await api.get('/easter-eggs/me');
        if (cancelled) return;
        setLive(res.data?.live || { active: false, enabled: false, window: null });
        setFoundKeys(Array.isArray(res.data?.found?.keys) ? res.data.found.keys : []);
      } catch {
        if (!cancelled) {
          setLive({ active: false, enabled: false, window: null });
          setFoundKeys([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [user?.id]);

  const placement = useMemo(() => {
    if (!pathname) return null;
    // prefer longest match
    const hits = PLACEMENTS.filter(p => pathname === p.match || pathname.startsWith(p.match + '/') || pathname.startsWith(p.match));
    hits.sort((a, b) => b.match.length - a.match.length);
    return hits[0] || null;
  }, [pathname]);

  if (!user) return null;
  if (loading) return null;
  if (!live?.active) return null;
  if (!placement) return null;

  const { eggKey, style } = placement;
  const alreadyFound = foundKeys.includes(eggKey);
  if (alreadyFound) return null;

  const src = `/assets/easter-eggs/${eggKey}.png`;

  return (
    <button
      type="button"
      onClick={async () => {
        if (saving) return;
        setSaving(true);
        try {
          const res = await api.post('/easter-eggs/found', { eggKey });
          if (res?.data?.ok) {
            setFoundKeys(prev => (prev.includes(eggKey) ? prev : [...prev, eggKey]));
          }
        } catch {
          // ignore (e.g. not live)
        } finally {
          setSaving(false);
        }
      }}
      title="Osterei"
      style={{
        position: 'fixed',
        zIndex: 9999,
        background: 'transparent',
        border: 'none',
        padding: 0,
        cursor: saving ? 'wait' : 'pointer',
        opacity: saving ? 0.7 : 1,
        ...style,
      }}
    >
      {imgOk ? (
        <img
          src={src}
          alt="Osterei"
          width={46}
          height={46}
          style={{ display: 'block' }}
          onError={() => setImgOk(false)}
        />
      ) : (
        <span style={{ fontSize: 32, lineHeight: '46px', display: 'block', width: 46, height: 46 }}>🥚</span>
      )}
    </button>
  );
}
